import { Plus, X } from "lucide-react";
import { useState } from "react";

import type { DocumentItem } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type AgentDraft,
  emptyReportTheme,
  type ReportThemeDraft,
} from "@/features/agents/components/AgentDraft";
import { useKbsDocuments } from "@/features/knowledge-bases/hooks/useDocuments";
import { cn } from "@/lib/utils";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
}

export function ReportThemeTab({ draft, onChange }: Props) {
  const cfg = draft.report_theme ?? emptyReportTheme();

  // Documentos das KBs associadas ao agente -> alimentam os seletores de
  // logo (imagens) e template (.html). Evita o usuario colar ID na mao
  // (e colar o ID da KB por engano, que derruba o template no fallback).
  const { documents, isLoading: docsLoading } = useKbsDocuments(draft.kb_ids);
  const htmlDocs = documents.filter((d) => d.mime_type === "text/html");
  const imageDocs = documents.filter((d) => d.mime_type.startsWith("image/"));

  function patch(next: Partial<ReportThemeDraft>) {
    onChange({ ...draft, report_theme: { ...cfg, ...next } });
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2 rounded-md border p-4">
        <Label
          htmlFor="theme-enabled"
          className="flex items-center gap-2 font-medium"
        >
          <input
            id="theme-enabled"
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          Aplicar identidade visual aos relatórios
        </Label>
        <p className="text-muted-foreground text-xs">
          Quando desligado, os relatórios usam o layout/cores padrão. Vale a
          partir da próxima geração.
        </p>
      </div>

      {cfg.enabled ? (
        <>
          {/* Cores */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
              Cores
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <ColorField
                label="Primária (títulos)"
                value={cfg.primary_color}
                onChange={(primary_color) => patch({ primary_color })}
              />
              <ColorField
                label="Destaque (links/detalhes)"
                value={cfg.accent_color}
                onChange={(accent_color) => patch({ accent_color })}
              />
              <ColorField
                label="Texto"
                value={cfg.text_color}
                onChange={(text_color) => patch({ text_color })}
              />
              <ColorField
                label="Fundo"
                value={cfg.background_color}
                onChange={(background_color) => patch({ background_color })}
              />
            </div>
          </section>

          {/* Fontes */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
              Fontes
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Fonte dos títulos"
                placeholder='ex.: Georgia, "Times New Roman", serif'
                value={cfg.heading_font}
                onChange={(heading_font) => patch({ heading_font })}
              />
              <TextField
                label="Fonte do corpo"
                placeholder="ex.: Arial, Helvetica, sans-serif"
                value={cfg.body_font}
                onChange={(body_font) => patch({ body_font })}
              />
            </div>
          </section>

          {/* Paleta de gráficos */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
              Paleta dos gráficos
            </h3>
            <p className="text-muted-foreground text-xs">
              Cores aplicadas automaticamente nas séries dos gráficos
              (render_chart). O agente pode sobrescrever por gráfico.
            </p>
            <PaletteEditor
              values={cfg.chart_palette}
              onChange={(chart_palette) => patch({ chart_palette })}
            />
          </section>

          {/* Logo + cabeçalho/rodapé */}
          <section className="space-y-3 rounded-md border p-4">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
              Logo e cabeçalho/rodapé
            </h3>
            <DocumentPicker
              label="Logo (imagem das KBs associadas)"
              value={cfg.logo_document_id}
              onChange={(logo_document_id) => patch({ logo_document_id })}
              documents={imageDocs}
              loading={docsLoading}
              placeholder="Selecione uma imagem"
              emptyHint="Nenhuma imagem nas KBs associadas. Suba a logo numa KB e associe-a na aba Conhecimento."
            />
            <HtmlField
              label="Cabeçalho (HTML opcional)"
              value={cfg.header_html}
              onChange={(header_html) => patch({ header_html })}
            />
            <HtmlField
              label="Rodapé (HTML opcional, repetido em cada página)"
              value={cfg.footer_html}
              onChange={(footer_html) => patch({ footer_html })}
            />
          </section>

          {/* Template próprio (Tier C) */}
          <section className="space-y-3 rounded-md border border-dashed p-4">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
              Template HTML próprio (avançado)
            </h3>
            <p className="text-muted-foreground text-xs">
              Opcional. Selecione um documento <code>.html</code> (Jinja) de uma
              base de conhecimento associada para usá-lo como layout do
              relatório. O recomendado é o template iniciar com{" "}
              <code>{`{% extends "report_base.html.j2" %}`}</code> para herdar a
              proteção de página/imagem. Se vazio, usa o layout padrão acima.
            </p>
            <DocumentPicker
              label="Template HTML (documentos .html das KBs associadas)"
              value={cfg.template_document_id}
              onChange={(template_document_id) =>
                patch({ template_document_id })
              }
              documents={htmlDocs}
              loading={docsLoading}
              placeholder="Selecione um template .html"
              emptyHint="Nenhum documento .html nas KBs associadas. Suba o template numa KB e associe-a na aba Conhecimento."
            />
          </section>
        </>
      ) : null}
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000";
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded border"
          aria-label={label}
        />
        <Input
          value={value}
          placeholder="#0b3a82"
          onChange={(e) => onChange(e.target.value)}
          className="font-mono"
        />
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange("")}
            aria-label="limpar"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function TextField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * Seletor de documento (logo/template) a partir das KBs associadas ao agente.
 * Mostra os documentos por NOME (não por ID colado). Se o ``value`` atual não
 * estiver entre os documentos das KBs associadas (ex.: ID antigo, ou o ID da
 * KB colado por engano), sinaliza com aviso — em vez de falhar em silêncio na
 * geração do relatório.
 */
function DocumentPicker({
  label,
  value,
  onChange,
  documents,
  loading,
  placeholder,
  emptyHint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  documents: DocumentItem[];
  loading: boolean;
  placeholder: string;
  emptyHint: string;
}) {
  const NONE = "__none__";
  const known = !value || documents.some((d) => d.id === value);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select
        value={value || NONE}
        onValueChange={(v) => onChange(v === NONE ? "" : v)}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— Nenhum (layout padrão) —</SelectItem>
          {documents.map((d) => (
            <SelectItem key={d.id} value={d.id}>
              {d.filename}
            </SelectItem>
          ))}
          {value && !known ? (
            <SelectItem value={value} disabled>
              ⚠ ID atual fora das KBs associadas
            </SelectItem>
          ) : null}
        </SelectContent>
      </Select>
      {loading ? (
        <p className="text-muted-foreground text-xs">Carregando documentos…</p>
      ) : documents.length === 0 ? (
        <p className="text-muted-foreground text-xs">{emptyHint}</p>
      ) : null}
      {!loading && value && !known ? (
        <p className="text-xs text-amber-600">
          O documento selecionado não está nas KBs associadas a este agente —
          provavelmente um ID antigo ou o ID de uma KB. Selecione um item da
          lista.
        </p>
      ) : null}
    </div>
  );
}

function HtmlField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="font-mono text-sm"
      />
    </div>
  );
}

function PaletteEditor({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("#0b3a82");

  function add() {
    const v = draft.trim().toLowerCase();
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(v) || values.includes(v)) return;
    onChange([...values, v]);
  }

  function remove(v: string) {
    onChange(values.filter((c) => c !== v));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(draft) ? draft : "#0b3a82"}
          onChange={(e) => setDraft(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded border"
          aria-label="nova cor"
        />
        <Input
          value={draft}
          placeholder="#0b3a82"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          className="font-mono"
        />
        <Button type="button" variant="outline" onClick={add}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div
        className={cn("flex flex-wrap gap-2", values.length === 0 && "hidden")}
      >
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1 font-mono">
            <span
              className="inline-block h-3 w-3 rounded-sm border"
              style={{ backgroundColor: v }}
            />
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="hover:bg-muted ml-1 rounded-full"
              aria-label={`remover ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}
